import ContainerLayout from '@/components/layouts/ContainerLayout';
import NavBar from '@/components/modules/navigation/navbar/NavBar/NavBar';
import { ROUTE_BUILDER } from '@/constants/pages';
import {
  createSerpCardClickHandlers,
  getDealsSearchHeadingProps,
  getDealsSerpBreadcrumbs,
  getSerpHeaderImage,
} from '@/features/deals/helpers';
import { Footer } from '@/features/footer';
import MegaMenu from '@/features/mega-menu/MegaMenu';
import { useMegaMenuContext } from '@/features/mega-menu/MegaMenu.hooks';
import { HideOnScrollWrapper } from '@/features/search-header/SearchHeader';
import SearchForm from '@/features/searchV2/modules/SearchForm/SearchForm';
import { isBrowserBackForward } from '@/helpers/browserNavigation';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION } from '@/hooks/search/useSearch.constants';
import { getKeywordWithSlug, getLocationWithSlug } from '@/hooks/search/useSearch.helpers';
import useMobileView from '@/hooks/useMobileView';
import { Fragment, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import NoSearchResults from '../components/NoSearchResults';
import SerpHeader from '../components/SerpHeader';
import { useDealsSearchContext } from '../hooks/useDealsSearch';
import { buildUrlSearchQuery } from '../hooks/useDealsSearch.helpers';
import useDealsSearchForm from '../hooks/useDealsSearchForm';
import DealsSectionPreview, { DealsSectionPreviewSkeleton } from '../modules/DealSectionPreview';
import { DEALS_GRADIENT_STOP } from './DealsSearchPage';
import DealsSearchSEO from './DealsSearchSEO';

const DealsSearchPreview = () => {
  const { isMobileView } = useMobileView();
  const megaMenuManager = useMegaMenuContext();
  const searchManager = useDealsSearchContext();
  const deals = useAppSelector((state) => state.dealsSearch);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const locationState = useLocation<object | undefined>()?.state;
  const { q, location } = searchManager;

  const {
    keywordAutoSuggestManager,
    locationAutoSuggestManager,
    dateTimePickerManager,
    isSmtOnlySearch,
    setIsSmtOnlySearch,
  } = useDealsSearchForm({
    searchManager,
    loadLocationFromHistory: false,
  });

  const breadcrumbs = getDealsSerpBreadcrumbs(q, location, 'deals_preview');
  const headingProps = getDealsSearchHeadingProps(searchManager);
  /**
   * Redirect user from the preview to the regular search results
   * when there is only one type of results (deals or sistaminuten).
   * previewRedirectType is computed in the reducer when preview results arrive.
   */
  useEffect(() => {
    const type = deals.previewRedirectType;

    if (!type) return;

    const what = (q || DEFAULT_SEARCH_KEYWORD).trim();
    const where = (location || DEFAULT_SEARCH_LOCATION).trim();
    const indexableKeyword = getKeywordWithSlug(what);
    const indexableLocation = getLocationWithSlug(where);

    const newIsIndexable = Boolean(indexableKeyword?.slug && indexableLocation?.slug);
    const search = buildUrlSearchQuery(
      { q: what, location: where, lat: searchManager.lat, lon: searchManager.lon, placeId: searchManager.placeId },
      newIsIndexable,
    );

    let pathname = undefined;

    if (newIsIndexable) {
      pathname =
        type === 'deals'
          ? ROUTE_BUILDER.dealsSerp(indexableKeyword.slug, indexableLocation.slug)
          : ROUTE_BUILDER.sistaMinutenSerp(indexableKeyword.slug, indexableLocation.slug);
    } else {
      pathname = type === 'deals' ? ROUTE_BUILDER.dealsSerpNoIndex() : ROUTE_BUILDER.sistaMinutenSerpNoIndex();
    }

    // The stored context drops `navigation`, so a back/forward to a preview that now redirects hands it on itself.
    history.replace({
      pathname,
      search,
      state: {
        ...locationState,
        searchContext: isBrowserBackForward(history) ? 'navigation' : deals.searchContext,
        redirectedFromPreview: type === 'sistaminuten',
      },
    });
  }, [deals.previewRedirectType]);

  /**
   * Computed fields for JSX render
   */
  const hasPreviewResults =
    deals.preview?.sections?.some((section) => section.items && section.items.length > 0) ?? false;
  const isFetching = deals.fetching || deals.previewRedirectType !== null;
  const isLoading = isFetching || searchManager.initialLoading;
  const showSkeleton = isLoading;
  const hasNoSearchResults = !isLoading && !hasPreviewResults;

  const { onDealClick, onMerchantClick } = createSerpCardClickHandlers(
    searchManager,
    deals,
    'deals_search_results',
    searchManager.saveDealsSearchState,
  );

  return (
    <div className="bg-gradient-deals" style={DEALS_GRADIENT_STOP}>
      <NavBar
        source="list"
        navClass="list-container navigation"
        defaultNavBarHeight={isMobileView ? 120 : 128}
        view="list"
        middleSlot={
          <SearchForm
            searchManager={searchManager}
            keywordAutoSuggestManager={keywordAutoSuggestManager}
            locationAutoSuggestManager={locationAutoSuggestManager}
            dateTimePickerManager={dateTimePickerManager}
            isSmtOnlySearch={isSmtOnlySearch}
            setIsSmtOnlySearch={setIsSmtOnlySearch}
          />
        }
        bottomSlot={
          <Fragment>
            {(() => {
              const megaMenu = (
                <MegaMenu
                  {...megaMenuManager}
                  renderNavigationContainer={(props) => {
                    const ref = props.navigationContainerRef.current;
                    return ref ? (
                      createPortal(<MegaMenu.Categories {...props} />, ref)
                    ) : (
                      <div
                        id={props.navigationContainerId}
                        className="mega-menu-navigation-container"
                        role="navigation">
                        <MegaMenu.Categories {...props} />
                      </div>
                    );
                  }}
                />
              );

              if (megaMenuManager.activeTab) {
                return megaMenu;
              }
              const height = megaMenuManager.navRef.current?.getBoundingClientRect()?.height ?? 56;
              return <HideOnScrollWrapper height={height}>{megaMenu}</HideOnScrollWrapper>;
            })()}
          </Fragment>
        }
        floatingSlot={<Fragment>{megaMenuManager.renderNavigationContainer(megaMenuManager)}</Fragment>}
      />

      <ContainerLayout onlyAbove2xl className="px-md py-lg md:py-xl">
        <SerpHeader
          breadcrumbs={breadcrumbs}
          heading={
            <Fragment>
              <span className="text-fg_primary font-bold">{headingProps.keyword}, </span>
              <span className="text-fg_secondary whitespace-nowrap">{headingProps.location}</span>
            </Fragment>
          }
          seoText={headingProps.description}
          image={getSerpHeaderImage(searchManager.type, isMobileView)}
        />
      </ContainerLayout>

      <ContainerLayout onlyAbove2xl>
        <div className="space-y-xl flex flex-col">
          {/* Deals Section */}

          {showSkeleton && <DealsSectionPreviewSkeleton cards={6} />}

          {!showSkeleton && (
            <Fragment>
              {hasPreviewResults &&
                deals.preview?.sections.map((section) => (
                  <DealsSectionPreview
                    key={section.id}
                    section={section}
                    dispatch={dispatch}
                    placeId={searchManager.placeId}
                    onCardClick={(props) => {
                      if (props.type === 'deal') {
                        onDealClick(props.item);
                      }
                      if (props.type === 'sistaminuten') {
                        onMerchantClick(props.item);
                      }
                    }}
                  />
                ))}
            </Fragment>
          )}
        </div>
      </ContainerLayout>
      {hasNoSearchResults && (
        <ContainerLayout onlyAbove2xl className="px-md py-xl">
          <NoSearchResults
            keyword={searchManager.q}
            location={searchManager.location}
            onCTAClick={() =>
              history.push(
                ROUTE_BUILDER.serp(
                  searchManager.q || DEFAULT_SEARCH_KEYWORD,
                  searchManager.location || DEFAULT_SEARCH_LOCATION,
                ),
              )
            }
          />
        </ContainerLayout>
      )}
      <ContainerLayout onlyAbove2xl className="pt-cv-margin">
        <div className="px-md">
          <Footer />
        </div>
      </ContainerLayout>
      <DealsSearchSEO {...searchManager} preview={deals.preview} list={deals.list} />
    </div>
  );
};

export default DealsSearchPreview;
